<template>
    <div class="w-100">
        <b-card title="" class="trigger-card p-2 mb-2" :class="{ 'error-card': !hasActions }" no-body>
            <template #header class="p-2">
                <div class="d-flex justify-content-between align-items-center w-100">
                    <h6 class="text-primary">Trigger <b>This Action</b></h6>
                </div>
            </template>
            <b-card-body class="p-2">
                <b-collapse visible id="actions">
                    <hr class="m-0 mb-3" />

                    <div v-for="(action, i) in actions" :key="action.type + i" class="pt-2 mx-lg-5 px-lg-4">
                        <b-row class="align-items-center w-100 ml-0">
                            <b-col class="p-0">
                                <validation-provider name="action_type" v-slot="{ errors }">
                                    <form-dropdown
                                        class="mb-0"
                                        :value="action.type"
                                        :options="actionsOptions"
                                        @input="(value) => onChangeAction(value, i)"
                                        placeholder="Select Action"
                                        :error="errors[0] || getEventError(action.type, 'required')"
                                    />
                                </validation-provider>
                            </b-col>
                        </b-row>
                        <b-row v-if="action.type === 'SMS'">
                            <b-col>
                                <div class="d-flex flex-column w-100 pt-2">
                                    <span class="d-block w-100">
                                        <div class="w-100">
                                            <form-search-field
                                                ref="search_field"
                                                class="w-100 mb-0 tag-search-field"
                                                :options="action.smsTagsOptions"
                                                @input="(val) => onSearchTag(val, false, i)"
                                                :value="action.tagSearch"
                                                @select="(text, value) => selectTag(text, value, i)"
                                                :rightButton="rightButtonConfig"
                                                :rightButton2="rightButtonConfig2"
                                                placeholder="Search Tag"
                                                @right-button-click="() => onRightButtonClick(action, i)"
                                                @right-button2-click="() => insertTextTag(action, i)"
                                                :isGrouped="true"
                                            />
                                        </div>
                                    </span>
                                    <validation-provider
                                        name="sms_action.message"
                                        v-slot="{ errors }"
                                        class="d-flex flex-column"
                                    >
                                        <form-textarea
                                            :value="action.sms"
                                            :rows="12"
                                            :error="
                                                calculateSmsLength(action.sms, tagsLengthObj) > 160
                                                    ? 'Sms must be less than 160 characters.'
                                                    : errors[0] || getEventError(action.sms, 'required')
                                            "
                                            @input="(val) => onChangeActionField(val, i, 'sms')"
                                            :ref="`textarea-${i}`"
                                        />
                                        <small class="text-secondary">
                                            (160 Characters Max - {{ getTotalCharacters(action.sms) }} Characters Used -
                                            {{ smsRemainingCharacters }}
                                            Characters Remaining)
                                        </small>
                                    </validation-provider>
                                </div>
                            </b-col>
                        </b-row>
                        <b-row v-if="action.type === 'EMAIL'">
                            <b-col md="12">
                                <div class="d-flex flex-column w-100 pt-2">
                                    <validation-provider name="recipients" v-slot="{ errors }">
                                        <form-input
                                            placeholder="Custom Recipients"
                                            class="email-input mb-2"
                                            :value="action.recipients"
                                            :error="
                                                errors[0] ||
                                                recipientsError(i) ||
                                                getEventError(action.recipients, 'commaSeparatedEmail')
                                            "
                                            @input="(val) => onChangeActionField(val, i, 'recipients')"
                                        />
                                    </validation-provider>
                                    <b-row class="no-gutters mb-2 flex-md-row flex-column">
                                        <b-col class="mr-md-2" v-if="!userCanUseUnlayer">
                                            <form-dropdown
                                                :value="action.selectedEmailResponse"
                                                :options="emailResponsesOptions"
                                                placeholder="-Append response-"
                                                @input="(value) => applyResponse(value, i)"
                                            />
                                        </b-col>
                                        <b-col class="mr-md-2" v-else>
                                            <form-dropdown
                                                :value="action.selectedUnlayerEmailResponse"
                                                :options="emailUnlayerResponsesOptions"
                                                placeholder="-Append response-"
                                                @input="(value) => applyUnlayerResponse(value, i)"
                                            />
                                        </b-col>
                                        <b-col class="mr-md-2" v-if="userCanUseUnlayer">
                                            <form-dropdown
                                                :value="action.selectedUnlayerPdfTemplate"
                                                :options="unlayerPdfTemplatesOptions"
                                                placeholder="-Attach PDF template-"
                                                @input="
                                                    (value) =>
                                                        onChangeActionField(value, i, 'selectedUnlayerPdfTemplate')
                                                "
                                            />
                                        </b-col>
                                        <b-col class="mr-md-2" v-else>
                                            <form-dropdown
                                                :value="action.selectedReportTemplate"
                                                :options="reportTemplatesOptions"
                                                placeholder="-Attach PDF template-"
                                                @input="(val) => onChangeActionField(val, i, 'selectedReportTemplate')"
                                            />
                                        </b-col>
                                        <b-col class="mr-md-2">
                                            <form-dropdown
                                                :value="action.selectedUploadedFile"
                                                :options="uploadedFilesOptions"
                                                @input="(val) => onChangeActionField(val, i, 'selectedUploadedFile')"
                                                placeholder="-Attach uploaded file-"
                                            />
                                        </b-col>
                                        <b-col class="mr-md-2">
                                            <form-dropdown
                                                v-model="action.selectedCalendarRange"
                                                :options="calendarRanges"
                                                placeholder="-Calendar to attach-"
                                                @input="(val) => onChangeActionField(val, i, 'selectedCalendarRange')"
                                            />
                                        </b-col>
                                        <b-col>
                                            <form-dropdown
                                                v-model="action.selectedReportSettings"
                                                :options="reportSettingsSelectOptions"
                                                placeholder="-Report to attach-"
                                                @input="(val) => onChangeActionField(val, i, 'selectedReportSettings')"
                                            />
                                        </b-col>
                                    </b-row>
                                    <validation-provider name="subject" v-slot="{ errors }">
                                        <form-input
                                            placeholder="Email Subject"
                                            class="email-input mb-2"
                                            :value="action.subject"
                                            @input="(val) => onChangeActionField(val, i, 'subject')"
                                            :error="errors[0] || getEventError(action.subject, 'required')"
                                        />
                                    </validation-provider>
                                    <validation-provider :name="'ck_editor' + i" v-slot="{ errors }">
                                        <form-ck-editor
                                            :class="[`ck_editor_` + i, 'position-relative', 'ckeditor-wrapper']"
                                            :editor-url="editorUrl"
                                            ref="editor"
                                            :value="action.email"
                                            :config="getConfig(i)"
                                            @ready="insertHtmlComponent"
                                            @input="(val) => onChangeActionField(val, i, 'email')"
                                            :error="errors[0] || getEventError(action.email, 'required')"
                                        >
                                            <span
                                                v-if="action.type === 'EMAIL'"
                                                role="toolbar"
                                                aria-labelledby="cke_430_label"
                                                class="cke_toolbar d-block w-100"
                                                id="cke_430"
                                            >
                                                <div class="search-field" @click="focusInput(i)">
                                                    <form-search-field
                                                        class="w-100 mb-0 tag-search-field"
                                                        :ref="`search_field_${i}`"
                                                        :options="action.htmlTagsOptions"
                                                        @input="(val) => onSearchTag(val, true, i)"
                                                        :value="action.tagSearch"
                                                        @select="(text, value) => selectTag(text, value, i)"
                                                        :rightButton="rightButtonConfig"
                                                        :rightButton2="rightButtonConfig2"
                                                        placeholder="Search Tag"
                                                        @right-button-click="() => onRightButtonClick(action, i)"
                                                        @right-button2-click="() => insertHtmlTag(action, i)"
                                                        :isGrouped="true"
                                                    />
                                                </div>
                                            </span>
                                        </form-ck-editor>
                                    </validation-provider>
                                </div>
                            </b-col>
                        </b-row>
                        <b-row v-if="action.type === 'PUSH'">
                            <b-col md="12">
                                <div class="d-flex flex-column w-100 pt-4">
                                    <validation-provider
                                        name="push_notification_actions[0].subject"
                                        v-slot="{ errors }"
                                        class="d-flex flex-column"
                                    >
                                        <form-input
                                            :value="action.subject"
                                            :error="errors[0] || getEventError(action.subject, 'required')"
                                            @input="(val) => onChangeActionField(val, i, 'subject')"
                                            placeholder="Subject"
                                        />
                                    </validation-provider>
                                    <span class="d-block w-100">
                                        <div class="w-100">
                                            <form-search-field
                                                ref="search_field"
                                                class="w-100 mb-0 tag-search-field"
                                                :options="action.pushTagsOptions"
                                                @input="(val) => onSearchTag(val, false, i)"
                                                :value="action.tagSearch"
                                                @select="(text, value) => selectTag(text, value, i)"
                                                :rightButton="rightButtonConfig"
                                                :rightButton2="rightButtonConfig2"
                                                placeholder="Search Tag"
                                                @right-button-click="() => onRightButtonClick(action, i)"
                                                @right-button2-click="() => insertTextTag(action, i, false)"
                                                :isGrouped="true"
                                            />
                                        </div>
                                    </span>
                                    <validation-provider
                                        name="push_notification_actions[0].message"
                                        v-slot="{ errors }"
                                        class="d-flex flex-column"
                                    >
                                        <form-textarea
                                            :value="action.message"
                                            :rows="12"
                                            :error="errors[0] || getEventError(action.message, 'required')"
                                            @input="(val) => onChangeActionField(val, i, 'message')"
                                            placeholder="Message"
                                            :ref="`textarea-${i}`"
                                        />
                                    </validation-provider>
                                </div>
                            </b-col>
                        </b-row>
                        <div
                            class="w-100 d-flex justify-content-center mt-2 mb-2 cursor-pointer"
                            @click="removeTriggerAction(i)"
                            v-if="!readonly"
                        >
                            <b-button
                                class="align-baseline btn-circle-minus mr-1"
                                title="Remove"
                                variant="danger"
                                size="xs"
                                ><font-awesome-icon :icon="['fas', 'minus']"></font-awesome-icon
                            ></b-button>
                            <span class="text-primary"> Remove Action </span>
                        </div>

                        <hr class="m-0 mb-1" />
                    </div>

                    <div class="w-100 d-flex justify-content-center mt-2" v-if="actions.length < 3 && !readonly">
                        <div @click="addTriggerAction" class="d-flex align-items-center">
                            <b-button
                                class="align-baseline btn-circle-plus mr-1"
                                title="Add"
                                variant="primary"
                                size="xs"
                                ><font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon
                            ></b-button>
                            <span class="text-primary cursor-pointer"> Add Action </span>
                        </div>
                    </div>
                </b-collapse>
            </b-card-body>
            <template v-if="!hasActions" #footer>
                <small class="text-danger">At least one action must be selected</small>
            </template>
        </b-card>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import api from '../../../../api.jsx';
import {
    TOOLBAR,
    FONT_NAMES,
    ACTIONS,
    EDITOR_URL,
    RIGHT_BUTTON_2_CONFIG,
    RIGHT_BUTTON_CONFIG,
} from '../helpers/constants';
import { calculateSmsLength } from '../helpers/helpers';

import TeleportV2 from 'vue2-teleport';
import FormSearchField from '../../../generic/form-search-field/form-search-field.vue';
import FormDropdown from '../../../generic/form-dropdown/form-dropdown.vue';
import FormInput from '../../../generic/form-input/form-input.vue';
import FormTextarea from '../../../generic/form-textarea/form-textarea.vue';
import FormCkEditor from '../../../generic/form-ck-editor/form-ck-editor.vue';
import { TRIGGER_ENDPOINT, APP_EMAIL_MOCKUP_RESPONSES } from '../../../../endpoints';
import { triggerMixin } from '../mixins/mixin.js';

export default {
    components: {
        TeleportV2,
        FormSearchField,
        FormDropdown,
        FormInput,
        FormTextarea,
        FormCkEditor,
    },
    mixins: [triggerMixin],
    data() {
        return {
            htmlMounted: false,
            sms_tags: [],
            html_tags: [],
            push_notification_tags: [],
        };
    },
    computed: {
        ...mapGetters('trigger', [
            'actions',
            'reportTemplatesOptions',
            'reportSettingsOptions',
            'uploadedFilesOptions',
            'emailResponsesOptions',
            'emailUnlayerResponsesOptions',
            'unlayerPdfTemplatesOptions',
            'tagsLengthObj',
            'hasActions',
            'allowedActions',
            'readonly',
        ]),
        ...mapGetters('header', ['userCanUseUnlayer']),
        actionsOptions() {
            return ACTIONS.filter((item) => this.allowedActions.includes(item.value));
        },
        calendarRanges() {
            return [
                { value: null, text: '-Calendar to attach-' },
                {
                    value: 'MONTH',
                    text: 'One Month',
                },
                {
                    value: '1_WEEK',
                    text: 'One Week',
                },
                {
                    value: '2_WEEKS',
                    text: 'Two Weeks',
                },
            ];
        },
        config() {
            return {
                allowedContent: true,
                entities: false,
                enterMode: 1,
                shiftEnterMode: 2,
                versionCheck: false,
                disableNativeSpellChecker: true,
                height: 400,
                font_names: FONT_NAMES,
                contentsCss: [window.CKEDITOR?.getUrl('contents.css'), '/static/css/ckeditor4.css'],
                toolbar: this.userCanUseUnlayer
                    ? TOOLBAR.filter((item) =>
                          item?.name && ['basicstyles', 'styles'].includes(item.name)
                      )
                    : TOOLBAR,
            };
        },
        editorUrl() {
            return EDITOR_URL;
        },
        rightButtonConfig() {
            return RIGHT_BUTTON_CONFIG;
        },
        rightButtonConfig2() {
            return RIGHT_BUTTON_2_CONFIG;
        },
        reportSettingsSelectOptions() {
            return [{ value: null, text: '-Report to attach-' }, ...this.reportSettingsOptions];
        },
        smsRemainingCharacters() {
            const action = this.actions.find((item) => item.type === 'SMS');
            if (action) {
                const length = 160 - this.calculateSmsLength(action.sms, this.tagsLengthObj);
                return length >= 0 ? length : 0;
            }
        },
    },
    mounted() {
        this.fetchTags();
    },
    methods: {
        ...mapActions('trigger', ['addAction', 'removeAction']),
        calculateSmsLength,
        addTriggerAction() {
            this.addAction();
            this.$store.commit('trigger/setEditing', true);
        },
        async applyResponse(value, i) {
            try {
                this.busy = true;
                const res = await api.post(`${TRIGGER_ENDPOINT()}append-email-response/`, { response_id: value });
                const actions = JSON.parse(JSON.stringify(this.actions));
                actions[i].email = res.data.description;
                actions[i].selectedEmailResponse = value;
                this.$store.commit('trigger/setActions', actions);
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async applyUnlayerResponse(value, i) {
            try {
                const res = await api.post(`${TRIGGER_ENDPOINT()}append-unlayer-email-response/`, {
                    response_id: value,
                });
                const actions = JSON.parse(JSON.stringify(this.actions));
                actions[i].email = res.data.html;
                actions[i].selectedUnlayerEmailResponse = value;
                this.$store.commit('trigger/setActions', actions);
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        getConfig(i) {
            return {
                ...this.config,
                ck_id: 'ck_editor_' + i,
            };
        },
        getTotalCharacters(sms) {
            if (this.tagsLengthObj) {
                return this.calculateSmsLength(sms, this.tagsLengthObj);
            }
            return 0;
        },
        async fetchTags() {
            try {
                const { data } = await api.get(`${TRIGGER_ENDPOINT()}tags/`);
                this.sms_tags = data.sms_tags;
                this.html_tags = data.html_tags;
                this.push_notification_tags = data.push_tags;
                this.$store.commit('trigger/setTagsLengthObj', data.tags_length);
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not fetch variables',
                });
            }
        },
        insertHtmlComponent() {
            this.htmlMounted = true;
            this.$nextTick(() => {
                this.$refs.editor?.forEach((item) => {
                    item.$forceUpdate();
                });
            });
        },
        insertTextTag(action, i, isSms = true) {
            if (action.selectedTag && action.tagSearch.trim()) {
                const actions = JSON.parse(JSON.stringify(this.actions));

                if (isSms) {
                    if (this.calculateSmsLength(action.sms, this.tagsLengthObj) > 160) {
                        return;
                    }
                    const text = this.insertAtCursor(this.$refs[`textarea-${i}`], action.selectedTag);
                    if (!text) {
                        if (action.sms) {
                            actions[i].sms = actions[i].sms + action.selectedTag;
                        } else {
                            actions[i].sms = action.selectedTag;
                        }
                    } else {
                        actions[i].sms = text;
                    }
                } else {
                    const text = this.insertAtCursor(this.$refs[`textarea-${i}`], action.selectedTag);
                    if (!text) {
                        if (action.message) {
                            actions[i].message = actions[i].message + action.selectedTag;
                        } else {
                            actions[i].message = action.selectedTag;
                        }
                    } else {
                        actions[i].message = text;
                    }
                }

                this.$store.commit('trigger/setActions', actions);
            }
        },
        insertHtmlTag(action, i) {
            if (window.CKEDITOR) {
                Object.values(CKEDITOR.instances).forEach((item, index) => {
                    const ck_id = item.config.ck_id;
                    if (ck_id === `ck_editor_${i}` && action.selectedTag) {
                        item.insertHtml(action.selectedTag);
                    }
                });
            }
        },
        onChangeActionField(value, index, field) {
            const actions = JSON.parse(JSON.stringify(this.actions));
            if (field === 'email') {
                if (value.trim() !== actions[index][field].trim()) {
                    this.$store.commit('trigger/setEditing', true);
                }
            }
            actions[index][field] = value;
            this.$store.commit('trigger/setActions', actions);
        },
        onChangeAction(value, index) {
            const hasSmsAction = this.actions.find((item) => item.type === 'SMS');
            if (value === 'SMS' && hasSmsAction && this.actions.length > 1 && this.actions[index].type !== 'SMS') {
                this.errorToast({ text: 'Only one sms per trigger allowed' });
                return;
            }
            const actions = this.actions.map((item, i) => {
                if (i === index) {
                    if (value === 'EMAIL') {
                        return {
                            type: value,
                            email: '',
                            subject: '',
                            recipients: '',
                            selectedCalendarRange: '',
                            selectedEmailResponse: '',
                            selectedReportTemplate: '',
                            selectedUploadedFile: '',
                            selectedReportSettings: '',
                            tagSearch: '',
                            selectedTag: '',
                            htmlTagsOptions: [],
                            selectedUnlayerEmailResponse: '',
                            selectedUnlayerReportTemplate: '',
                        };
                    } else if (value === 'SMS') {
                        return {
                            type: value,
                            sms: '',
                            tagSearch: '',
                            selectedTag: '',
                            smsTagsOptions: [],
                        };
                    } else {
                        return {
                            type: value,
                            message: '',
                            subject: '',
                            tagSearch: '',
                            selectedTag: '',
                            pushTagsOptions: [],
                        };
                    }
                }
                return item;
            });
            if (value === 'EMAIL') {
                this.htmlMounted = false;
                this.$nextTick(() => {
                    this.htmlMounted = true;
                });
            }
            this.$store.commit('trigger/setActions', actions);
            this.$store.commit('trigger/setEditing', true);
        },
        onSearchTag(value, isHtml, index) {
            const actions = JSON.parse(JSON.stringify(this.actions));
            actions[index].tagSearch = value;
            if (!value.trim()) {
                actions[index].selectedTag = '';
            }
            let tags = [];
            if (actions[index].type === 'PUSH') {
                tags = this.push_notification_tags;
            } else {
                tags = isHtml ? this.html_tags : this.sms_tags;
            }
            const options = tags.map((item) => {
                return {
                    text: `${item.tag}: ${item.description}`,
                    value: item.tag,
                    group: item.group.toLowerCase(),
                };
            });
            const filteredOptions = options.filter(
                (item) => item.text.toLowerCase().indexOf(value.trim().toLowerCase()) > 0
            );
            if (
                options
                    .map((item) => {
                        return {
                            text: `${item.tag}: ${item.description}`,
                            value: item.tag,
                            group: item.group.toLowerCase(),
                        };
                    })
                    .some((item) => item.text.toLowerCase() !== value.toLowerCase())
            ) {
                actions[index].selectedTag = '';
            }
            if (isHtml) {
                actions[index].htmlTagsOptions = filteredOptions;
            } else if (actions[index].type === 'PUSH') {
                actions[index].pushTagsOptions = filteredOptions;
            } else {
                actions[index].smsTagsOptions = filteredOptions;
            }
            this.$store.commit('trigger/setActions', actions);
        },
        recipientsError(i) {
            const recipients = this.actions[i].recipients?.split(',');
            if (recipients && recipients.length > 3) {
                return `${recipients.length}/3 You have exceeded the maximum number of 3 custom recipients`;
            }
        },
        rerenderHtmlComponent() {
            this.htmlMounted = false;
            this.$nextTick(() => {
                this.htmlMounted = true;
            });
        },
        removeTriggerAction(i) {
            this.removeAction(i);
            this.rerenderHtmlComponent();
            this.$store.commit('trigger/setEditing', true);
        },
        selectTag(text, value, index) {
            const actions = JSON.parse(JSON.stringify(this.actions));
            actions[index].tagSearch = text;
            actions[index].selectedTag = value;
            this.$store.commit('trigger/setActions', actions);
        },
        onRightButtonClick(action, index) {
            const isEmail = action.type === 'EMAIL';
            const isPush = action.type === 'PUSH';
            const actions = JSON.parse(JSON.stringify(this.actions));
            actions[index].tagSearch = '   ';
            if (!actions[index].tagSearch.trim()) {
                actions[index].selectedTag = '';
            }
            let tags = [];
            if (isPush) {
                tags = this.push_notification_tags;
            } else if (isEmail) {
                tags = this.html_tags;
            } else {
                tags = this.sms_tags;
            }
            const options = tags.map((item) => {
                return {
                    text: `${item.tag}: ${item.description}`,
                    value: item.tag,
                    group: item.group.toLowerCase(),
                };
            });
            if (isPush) {
                actions[index].pushTagsOptions = options;
            } else if (isEmail) {
                actions[index].htmlTagsOptions = options;
            } else {
                actions[index].smsTagsOptions = options;
            }
            this.$store.commit('trigger/setActions', actions);
        },
        focusInput(i) {
            const search = this.$refs[`search_field_${i}`];
            const component = search.length > 0 && search[0];
            if (component) {
                component.$el.querySelector('input').focus();
                component.$el.querySelector('input').select();
            }
        },
        insertAtCursor(wrapper, textToInsert) {
            if (wrapper !== undefined) {
                wrapper = wrapper[0];
                if (wrapper.$children[0]) {
                    if (wrapper.$children[0].$children[0]) {
                        if (wrapper.$children[0].$children[0].$children[0]) {
                            const textarea = wrapper.$children[0].$children[0].$children[0].$el;
                            const value = textarea.value;
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            textarea.value = value.slice(0, start) + textToInsert + value.slice(end);
                            textarea.selectionStart = textarea.selectionEnd = start + textToInsert.length;
                            return textarea.value;
                        }
                    }
                }
            }

            return false;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../styles.scss';
</style>
